import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  TextareaAutosize,
  Collapse,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import down_arrow from "assets/down_arrow.svg";
import right_arrow from "assets/right_arrow.svg";
import {
  ContentItem,
  ContentItemData,
  ContentItemOverview,
  ContentSyncJob,
  ContentUpdateAction,
  DetailedContentItem,
  Developer,
  NotesObj,
  User,
} from "interfaces";
import userselect from "assets/userselect.svg";
import "../ContentTable/ContentTable.css";
import alertOutlined from "assets/AlertOutlined.svg";
import bugOutlined from "assets/BugOutlined.svg";
import likeOutlined from "assets/LikeOutlined.svg";
import plusOutlined from "assets/PlusOutlined.svg";
import stopOutlined from "assets/StopOutlined.svg";
import { LargeStatCard } from "cms/components/dashboard/LargeStatCard/LargeStatCard";
import calanderSelect from "assets/calanderSelect.svg";
import { Button as AntButton, DatePicker, Tabs, Tooltip } from "antd";
import eye from "assets/eye.svg";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { EyeOutlined, LoadingOutlined, SyncOutlined } from "@ant-design/icons";
import { axiosInstance } from "@refinedev/simple-rest";
import { API_URL } from "utils/constants";
const searchValue = [
  { label: "Amadeus", iconName: userselect },
  { label: "To Kill a Mockingbird", iconName: userselect },
  { label: "Toy Story 3", iconName: userselect },
  { label: "Logan", iconName: userselect },
];

const Textarea = styled(TextareaAutosize)(
  ({ theme }) => `
    box-sizing: border-box;
    width: 100%;
    font-family: 'Work Sans', 'sans-serif';
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    padding: 8px 14px;
    border-radius: 8px;
    border-color:#B2BBC6;
    color:#1D242D;
    &:placeholder{
      color:#B2BBC6;
    }
    &:focus-visible {
      outline: 0;
    }
  `
);

interface RowProps {
  row: ContentItemOverview;
  rowDetails: ContentItemData | null;
  isSelected: boolean;
  isSyncing: boolean;
  onSelect: (id: number) => void;
  onExpand: (row: ContentItemOverview) => Promise<void>;
  onSync: (row: ContentItemOverview) => Promise<void>;
  open: boolean;
  updateContent: (action: ContentUpdateAction, item: ContentItemOverview) => void;
  onIndicatorChange: (id: number, newIndicator: string) => void;
  indicatorIcon: string;
  showCheckboxes: boolean;
}
const Row: React.FC<RowProps> = ({
  row,
  rowDetails,
  isSelected,
  isSyncing,
  onSelect,
  onExpand,
  onSync,
  open,
  updateContent,
  onIndicatorChange,
  showCheckboxes,
}) => {
  const [view, setView] = useState("list");
  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    nextView: string
  ) => {
    setView(nextView);
    onIndicatorChange(row.id, nextView);
  };

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
  const [selectedDeveloper, setSelectedDeveloper] = useState<Developer | null>(
    null
  );
  const [developers, setDevelopers] = useState<Developer[]>([]);

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
  };

  const handleRowClick = (event: React.MouseEvent) => {
    if (!(event.target as HTMLElement).closest(".expand-button")) {
      onSelect(row.id);
    }
  };

  const handleExpand = async () => {
    setIsLoadingDetails(true);
    await onExpand(row);
    setIsLoadingDetails(false);
  };

  useEffect(() => {
    if (rowDetails) {
      setDevelopers(rowDetails.developers);
      setSelectedDeveloper(row.assigned_to);
    }
  }, [rowDetails]);

  useEffect(() => {
    if (row) {
      if (row.assigned_due) setSelectedDate(dayjs(row.assigned_due));
    }
  }, [row]);

  const navigate = useNavigate();

  const handleViewClick = () => {
    navigate(`/content/${row.id}`);
  };

  const handleSelectDeveloper = (
    developer: { label: string; value: number; iconName: string } | null
  ) => {
    setSelectedDeveloper(
      developer
        ? developers.find((d) => d.id === developer.value) ?? null
        : null
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Assigned":
      case "Reassigned":
        return "#0094FD";
      case "Limbo":
      case "Not Required":
        return "#7E7E7E";
      case "Revisit":
        return "#A400C5";
      case "Issue":
        return "#ED0000";
      case "Review":
        return "#F4C401";
      case "Prepped":
      case "Uploaded":
        return "#1F9C00";
      default:
        return "#7E7E7E";
    }
  };

  const percentComplete =
    rowDetails && rowDetails?.completions.total !== 0
      ? Math.round(
          (rowDetails.completions.complete / rowDetails.completions.total) * 100
        )
      : 0;
  const percentIncomplete = 100 - percentComplete;

  return (
    <React.Fragment>
      <TableRow
        onClick={handleRowClick}
        sx={{ "& > *": { borderBottom: "unset" } }}
      >
        <TableCell padding="checkbox">
          {/* {showCheckboxes && (
            <Checkbox
              color="primary"
              checked={isSelected}
              onClick={(event) => {
                event.stopPropagation();
              }}
              onChange={() => onSelect(row.id)}
            />
          )} */}
        </TableCell>
        <TableCell align="left">{row.subject.name}</TableCell>
        <TableCell align="left">{row.section.name}</TableCell>
        <TableCell align="left">{row.template.db_name}</TableCell>
        <TableCell align="center" style={{ color: getStatusColor(row.status) }}>
          {row.status}
        </TableCell>
        <TableCell align="center">{row.assigned_to?.name}</TableCell>
        <TableCell align="center">
          {selectedDate ? selectedDate.format("YYYY/MM/DD") : ""}
        </TableCell>
        <TableCell align="center">
          {row.indicator ? (
            <img src={row.indicator} alt="" className="content-indicators" />
          ) : (
            ""
          )}
        </TableCell>
        <TableCell align="center">
          <Tooltip
            title={
              row.last_synced
                ? `Last synced ${dayjs(row.last_synced).format("DD/MM/YYYY")}`
                : "Never synced"
            }
          >
            <SyncOutlined
              spin={isSyncing}
              onClick={(event) => {
                event.stopPropagation();
                onSync(row);
                // onExpand(row);
              }}
            />
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          {isLoadingDetails ? (
            <SyncOutlined spin />
          ) : (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleExpand();
              }}
            >
              <img
                src={open ? down_arrow : right_arrow}
                alt={open ? "Collapse" : "Expand"}
              />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
            <Box className="nested-table-block">
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box className="nested-table-block">
                  <Table aria-label="purchases">
                    <TableBody>
                      <TableRow>
                        <TableCell
                          className="custom-td"
                          component="td"
                          scope="row"
                        >
                          {rowDetails && (
                            <div className="custom-td custom-td-doughnut">
                              <LargeStatCard
                                title=""
                                chartType="doughnut"
                                dataset={{
                                  datasets: [
                                    {
                                      data: [
                                        percentComplete,
                                        percentIncomplete,
                                      ],
                                      backgroundColor: [
                                        "#2f8519",
                                        percentComplete === 0
                                          ? "#ff0000"
                                          : "#ffff",
                                      ],
                                      borderWidth: 1,
                                      borderColor: ["#2f8519", "#888"],
                                    },
                                  ],
                                }}
                                centerText={percentComplete + ""}
                              />
                              <p>% completed</p>
                            </div>
                          )}
                        </TableCell>

                        <TableCell className="custom-td">
                          <div className="td-select-filed-group">
                            <div className="input-select-search-filed">
                              <label className="input-label" htmlFor="">
                                Assigned to
                              </label>

                              <Autocomplete
                                disablePortal
                                value={
                                  selectedDeveloper
                                    ? {
                                        label: selectedDeveloper.name,
                                        value: selectedDeveloper.id,
                                        iconName: userselect,
                                      }
                                    : null
                                }
                                options={developers.map((d) => {
                                  return {
                                    label: d.name,
                                    value: d.id,
                                    iconName: userselect,
                                  };
                                })}
                                onChange={(_, value) =>
                                  handleSelectDeveloper(value)
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.value === value.value
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    InputProps={{
                                      ...params.InputProps,

                                      startAdornment: (
                                        <InputAdornment
                                          position="start"
                                          className="prefix-icon"
                                        >
                                          <img
                                            src={userselect}
                                            alt="user select"
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                                renderOption={(props, option) => (
                                  <ListItem
                                    className="autocomplete-list-items"
                                    {...props}
                                  >
                                    <ListItemIcon className="autocomplete-list-items-icons">
                                      <img
                                        src={`${option?.iconName}`}
                                        alt="Close Icon"
                                      />
                                    </ListItemIcon>

                                    <ListItemText
                                      sx={{ padding: "0px", margin: "0px" }}
                                      primary={option.label}
                                    />
                                  </ListItem>
                                )}
                              />
                            </div>

                            <div className="input-select-search-filed">
                              <label className="input-label">Due by</label>
                              <div className="datepicker_wrapper">
                                <DatePicker
                                  open={isDatePickerOpen}
                                  onOpenChange={setIsDatePickerOpen}
                                  value={selectedDate}
                                  onChange={handleDateChange}
                                  format="YYYY/MM/DD"
                                />
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell
                          className="custom-td"
                          align="right"
                          colSpan={2}
                        >
                          <div className="tabs-issue-wrapper">
                            <p className="collapse-td-label">Raise Issue</p>

                            <Tabs
                              tabPosition={"left"}
                              items={[
                                "Revisit",
                                "General Issue",
                                "Technical Issue",
                              ].map((label, index) => ({
                                label,

                                key: index.toString(),

                                children: (
                                  <div className="tab-panel-content-block">
                                    <div>
                                      <p className="comment-text">
                                        <Textarea
                                          aria-label="minimum height"
                                          minRows={1}
                                          placeholder="Enter notes"
                                        />
                                      </p>
                                    </div>

                                    <div className="save-button-block">
                                      <Button>Save</Button>
                                    </div>
                                  </div>
                                ),
                              }))}
                            />
                          </div>
                        </TableCell>
                        <TableCell className="custom-td" align="right">
                          <div className="indicator-tabs">
                            <p className="collapse-td-label">Indicator</p>

                            <ToggleButtonGroup
                              orientation="vertical"
                              value={view}
                              exclusive
                              onChange={handleChange}
                            >
                              <ToggleButton value="alert" aria-label="list">
                                <img src={alertOutlined} alt="AlertOutlined" />
                              </ToggleButton>

                              <ToggleButton value="bug" aria-label="module">
                                <img src={bugOutlined} alt="bugOutlined" />
                              </ToggleButton>

                              <ToggleButton value="like" aria-label="quilt">
                                <img src={likeOutlined} alt="likeOutlined" />
                              </ToggleButton>

                              <ToggleButton value="add" aria-label="quilt">
                                <img src={plusOutlined} alt="plusOutlined" />
                              </ToggleButton>

                              <ToggleButton value="stop" aria-label="quilt">
                                <img src={stopOutlined} alt="stopOutlined" />
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </div>
                        </TableCell>

                        <TableCell className="custom-td" align="right">
                          <div className="note-block">
                            <p className="collapse-td-label text-left">Notes</p>

                            <div className="note-tabs-wrap">
                              <Tabs
                                defaultActiveKey="1"
                                centered
                                items={[
                                  "Admin",
                                  "Team Leader",
                                  "Developer",
                                ].map((label, index) => ({
                                  label,

                                  key: index.toString(),

                                  children: (
                                    <div className="tab-panel-content-block">
                                      <div className="input-form-field-wrapper">
                                        <Textarea
                                          aria-label="minimum height"
                                          minRows={1}
                                          placeholder="Enter notes"
                                        />
                                      </div>

                                      <div className="save-button-block">
                                        <Button>Save</Button>
                                      </div>
                                    </div>
                                  ),
                                }))}
                              />
                            </div>
                          </div>
                        </TableCell>

                        <TableCell align="right">
                          {rowDetails?.capture_id && (
                            <div className="action-col">
                              <AntButton
                                href={`https://docs.google.com/spreadsheets/d/${rowDetails?.capture_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                type="primary"
                                icon={<EyeOutlined />}
                                shape="round"
                                className="custom-ant-btn"
                              >
                                {" "}
                                <strong>View</strong>
                              </AntButton>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

interface ContentTableProps {
  data: ContentItemOverview[];
  detailedData: Record<number, ContentItemData>;
  totalCount: number;
  syncJobs: ContentSyncJob[];
  fetchData: (page: number, itemsPerPage: number) => void;
  fetchItem: (row: number) => Promise<void>;
  updateContent: (action: ContentUpdateAction, item: ContentItemOverview) => void;
  onIndicatorChange: (id: number, newIndicator: string) => void;
  setIndicators: (id: any) => void;
  onSelectedItemsChange: (selectedItems: number[]) => void;
  onExpandedItemChange: (expandedItem: number | null) => void;
  onSyncStart: () => void;
}

const ContentTable: React.FC<ContentTableProps> = ({
  data,
  detailedData,
  totalCount,
  syncJobs,
  fetchData,
  fetchItem,
  updateContent,
  onSelectedItemsChange,
  onExpandedItemChange,
  onSyncStart,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const [indicators, setIndicators] = useState<{ [key: number]: string }>({});
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [activeJobIds, setActiveJobIds] = useState<number[]>([]);

  const indicatorImageMap: { [key: string]: string } = {
    alert: alertOutlined,
    bug: bugOutlined,
    like: likeOutlined,
    add: plusOutlined,
    stop: stopOutlined,
  };
  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    const newValue = Number(event.target.value);
    setItemsPerPage(newValue);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (syncJobs) {
      let currentJobs: number[] = [];
      for (const job of syncJobs) {
        if (!["completed", "failed"].includes(job.status)) {
          currentJobs.push(parseInt(job.meta.content_id + ""));
        }
      }
      setActiveJobIds(currentJobs);
    }
  }, [syncJobs]);

  // useEffect(() => {
  //   fetchData(currentPage, itemsPerPage === -1 ? totalCount : itemsPerPage);
  // }, [currentPage, itemsPerPage, fetchData, totalCount]);

  // const handleUpdateUser = (item: ContentItem, newAssignedUser: User) => {
  //   updateContent({
  //     ...item,
  //     assigned: newAssignedUser,
  //   });
  // };

  // const handleUpdateDueDate = (item: ContentItem, newDate: Date) => {
  //   updateContent({
  //     ...item,
  //     assigned_due: newDate,
  //   });
  // };

  // const handleRaiseIssue = (item: ContentItem, newNotes: string) => {
  //   updateContent({
  //     ...item,
  //     issue: {
  //       ...item.issue,
  //       notes: newNotes,
  //     },
  //   });
  // };

  // const handleUpdateNotes = (
  //   item: ContentItem,
  //   noteType: keyof NotesObj,
  //   newNote: string,
  //   userId: number,
  //   date: string
  // ) => {
  //   updateContent({
  //     ...item,
  //     notes: {
  //       ...item.notes,
  //       [noteType]: {
  //         ...item.notes[noteType],
  //         a: userId,
  //         c: date,
  //         d: newNote,
  //       },
  //     },
  //   });
  // };

  const handleIndicatorChange = (id: number, newIndicator: string) => {
    setIndicators((prev: any) => ({
      ...prev,
      [id]: newIndicator,
    }));
  };
  const handleSelectAll = (event: { target: { checked: any } }) => {
    const visibleItems = data
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      .map((item) => item.id);

    const allVisibleSelected = visibleItems.every((id) =>
      selectedItems.includes(id)
    );

    const newSelectedItems = allVisibleSelected
      ? selectedItems.filter((id) => !visibleItems.includes(id))
      : Array.from(new Set([...selectedItems, ...visibleItems]));

    setSelectedItems(newSelectedItems);
    setShowCheckboxes(true);
    onSelectedItemsChange(newSelectedItems);
  };

  const handleSelectRow = (id: number) => {
    // setSelectedItems((prevSelected) => {
    //   const updatedSelected = prevSelected.includes(id)
    //     ? prevSelected.filter((item) => item !== id)
    //     : [...prevSelected, id];
    //   onSelectedItemsChange(updatedSelected);
    //   setShowCheckboxes(true);
    //   return updatedSelected;
    // });
  };

  const handleExpandRow = async (row: ContentItemOverview) => {
    if (row && !(row.id in detailedData)) {
      await fetchItem(row.id);
    }
    const updatedExpandedItem = expandedItem === row.id ? null : row.id;
    setExpandedItem(updatedExpandedItem);
    onExpandedItemChange(updatedExpandedItem);
  };

  const handleSyncRow = async (row: ContentItemOverview) => {
    const syncBody = {
      subject: row.subject,
      section: row.section,
      template: { id: row.template.id, name: row.template.db_name },
      content_id: row.id,
    };
    await axiosInstance.post(`${API_URL}/cms/admin/content/sync`, syncBody, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    onSyncStart();
  };

  const handlePageChange = (event: any, value: number) => {
    setCurrentPage(value);
  };

  return (
    <>
      <div className="content-table-wrapper">
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell>
                  {/* <Checkbox
                    color="primary"
                    checked={selectedItems.length === data.length}
                    indeterminate={
                      selectedItems.length > 0 &&
                      selectedItems.length < data.length
                    }
                    onChange={handleSelectAll}
                  /> */}
                </TableCell>
                <TableCell align="left">Subject</TableCell>
                <TableCell align="left">Section</TableCell>
                <TableCell align="left">Content Type</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Assigned</TableCell>
                <TableCell align="center">Assigned Due</TableCell>
                <TableCell align="center">Indicator</TableCell>
                <TableCell align="center">Sync</TableCell>
                <TableCell align="center">Expand</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data
                .slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage
                )
                .map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    rowDetails={
                      row.id in detailedData ? detailedData[row.id] : null
                    }
                    isSelected={selectedItems.includes(row.id)}
                    isSyncing={activeJobIds.includes(row.id)}
                    onSelect={handleSelectRow}
                    onExpand={handleExpandRow}
                    onSync={handleSyncRow}
                    updateContent={updateContent}
                    open={expandedItem === row.id}
                    indicatorIcon={
                      row.indicator ? indicatorImageMap[row.indicator] : ""
                    }
                    showCheckboxes={showCheckboxes}
                    onIndicatorChange={function (
                      id: number,
                      newIndicator: string
                    ): void {
                      throw new Error("Function not implemented.");
                    }}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div className="pagination-wrapper">
        <Select
          labelId="rows-per-page-label"
          id="rows-per-page"
          value={itemsPerPage}
          onChange={handleChangeRowsPerPage}
          className="select-per-page"
        >
          {[5, 10, 25].map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
          {totalCount <= 50 && <MenuItem value={-1}>All</MenuItem>}
        </Select>
        <Stack spacing={2}>
          <Pagination
            count={Math.ceil(
              totalCount / (itemsPerPage === -1 ? totalCount : itemsPerPage)
            )}
            shape="rounded"
            page={currentPage}
            onChange={handlePageChange}
          />
        </Stack>
      </div>
    </>
  );
};

export default ContentTable;
